import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { IoArrowBack, IoLockClosed } from 'react-icons/io5'

import PasswordEyeInput from '@/components/forms/PasswordEyeInput/PasswordEyeInput'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import useAuthStore, { type ChangePasswordRequest } from '@/stores/AuthStore'
import routes from '@/routes/routes'

import './ChangePassword.css'

const ChangePassword = () => {
    const navigate = useNavigate()
    const { t } = useTranslation('common')
    const { changePasswordLogged, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<ChangePasswordRequest>({
        old_password: '',
        new_password1: '',
        new_password2: '',
    })

    useEffect(() => {
        if (!isLogged) {
            navigate(routes.login)
        }
    }, [isLogged])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: ChangePasswordRequest) => {
        const { old_password, new_password1, new_password2 } = formData
        
        if (!old_password || !new_password1 || !new_password2) {
            return { isValid: false, message: t('missing_fields') }
        }
        
        // Password confirmation validation
        if (new_password1 !== new_password2) {
            return { isValid: false, message: t('passwords_do_not_match') }
        }

        // Check if new password is different from old password
        if (old_password === new_password1) {
            return { isValid: false, message: 'La nueva contraseña debe ser diferente de la contraseña actual' }
        }
        
        // Password strength validation (optional)
        if (new_password1.length < 8) {
            return { isValid: false, message: 'La contraseña debe tener al menos 8 caracteres' }
        }
        
        return { isValid: true, message: '' }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault()

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        try {
            const success = await changePasswordLogged(formData)
            if (success) {
                navigate(routes.profile)
                return
            }
        } catch (error) {
            console.error('Change password error:', error)
        }
    }

    return (
        <div className="auth_page">
            <div className="auth_container">
                <div className="auth_header">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(routes.profile)}
                    >
                        <IoArrowBack size={16} />
                        Volver al Perfil
                    </Button>
                </div>

                <div className="auth_card">
                    <div className="auth_brand">
                        <div className="brand_logo">
                            <IoLockClosed size={32} />
                        </div>
                        <h1>Cambiar Contraseña</h1>
                        <p>Actualice la contraseña de su cuenta para mayor seguridad</p>
                    </div>

                    <form className="auth_form" onSubmit={handleSubmit}>
                        <PasswordEyeInput
                            name="old_password"
                            value={formData.old_password}
                            setValue={(value) => handleFormDataChange('old_password', value)}
                            label="Contraseña Actual"
                            placeholder="Ingrese su contraseña actual"
                        />

                        <PasswordEyeInput
                            name="new_password1"
                            value={formData.new_password1}
                            setValue={(value) => handleFormDataChange('new_password1', value)}
                            label="Nueva Contraseña"
                            placeholder="Ingrese su nueva contraseña"
                        />

                        <PasswordEyeInput
                            name="new_password2"
                            value={formData.new_password2}
                            setValue={(value) => handleFormDataChange('new_password2', value)}
                            label="Confirmar Nueva Contraseña"
                            placeholder="Confirme su nueva contraseña"
                        />

                        <div className="change-password-info">
                            <p><strong>Requisitos de Contraseña:</strong></p>
                            <p>• Al menos 8 caracteres de longitud</p>
                            <p>• Diferente de su contraseña actual</p>
                            <p>• Ambos campos de contraseña deben coincidir</p>
                        </div>

                        <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            disabled={isLoading}
                            className="auth_submit"
                        >
                            {isLoading ? <Spinner size="sm" variant="secondary" /> : 'Actualizar Contraseña'}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default ChangePassword
